/*
 * Copyright 2026 Sony Corporation
 */

import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader';
import { BackgroundMaterial } from '@babylonjs/core/Materials/Background/backgroundMaterial';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { RenderTargetTexture } from '@babylonjs/core/Materials/Textures/renderTargetTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Tools } from '@babylonjs/core/Misc/tools';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { AssetContainer } from '@babylonjs/core/assetContainer';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';

import { Disposables } from './disposables';
import { extentOfDome, holdProjectedGround } from './projected-ground';
import { createSceneInstance } from './scene-instance';
import { snapshotGlobals } from './globals';
import type { Bounds, DefaultView, SceneInstance, SceneSource, StageContext, StagedScene } from './types';

// The .babylon loader resolves types through a registry that only the concrete
// class's own side-effect import fills. Missing one does not throw: the loader
// silently yields an empty container, so every type a saved scene can contain
// is pulled in here deliberately.
import '@babylonjs/core/Loading/Plugins/babylonFileLoader';
import '@babylonjs/core/Materials/colorCurves';
import '@babylonjs/core/Materials/standardMaterial';
import '@babylonjs/core/Materials/PBR/pbrMaterial';
import '@babylonjs/core/Meshes/groundMesh';

/**
 * A backdrop, not scenery: either a box riding on the camera, or a ground-
 * projected dome. Both say how far the sky is drawn, not how big the scene is,
 * and neither is somewhere the world's transform can move.
 */
const isBackdrop = (mesh: AbstractMesh): boolean =>
  (mesh instanceof Mesh && mesh.infiniteDistance) || mesh.material instanceof BackgroundMaterial;

export interface BabylonSourceOptions {
  id: string;
  name: string;
  /** Resolved at stage time, so a signed URL is never held past its lifetime. */
  url: () => string;
  bounds?: Bounds;
  defaultView?: DefaultView;
  /** Mesh names the editor's shadow generator should render. */
  shadowCasterNames?: readonly string[];
  /** For the few things serialization cannot round-trip (scene-switch.md §9.1). */
  fixups?: (instance: LoadedScene) => void;
}

export interface LoadedScene {
  container: AssetContainer;
  mesh: (name: string) => AbstractMesh | undefined;
  own: Disposables;
}

export class SceneLoadError extends Error {
  constructor(
    message: string,
    readonly cause?: unknown
  ) {
    super(message);
    this.name = 'SceneLoadError';
  }
}

const boundsOf = (container: AssetContainer, declared: Bounds | undefined): Bounds => {
  if (declared) {
    return declared;
  }

  // A backdrop is drawn far enough out to fill the view whatever the camera
  // does, so its size says nothing about the scene. Counting it would put the
  // camera far enough out to frame a room the scene does not have.
  const meshes = container.meshes.filter((mesh) => mesh.getTotalVertices() > 0 && !isBackdrop(mesh));
  if (meshes.length === 0) {
    return { center: Vector3.Zero(), radius: 1 };
  }

  let min = meshes[0].getBoundingInfo().boundingBox.minimumWorld.clone();
  let max = meshes[0].getBoundingInfo().boundingBox.maximumWorld.clone();
  for (const mesh of meshes.slice(1)) {
    const { minimumWorld, maximumWorld } = mesh.getBoundingInfo().boundingBox;
    min = Vector3.Minimize(min, minimumWorld);
    max = Vector3.Maximize(max, maximumWorld);
  }

  const center = Vector3.Center(min, max);
  return { center, radius: Math.max(max.subtract(min).length() / 2, Number.EPSILON) };
};

/**
 * Refuses any request other than the one URL the scene was loaded from.
 *
 * A self-contained scene has nothing legitimate to fetch, so this is an absolute
 * rule rather than an allowlist (scene-switch.md §2 rule 8). The hook is a global
 * static, so it is restored the moment the load finishes.
 */
const withSingleUrlGuard = async <T>(allowed: string, load: () => Promise<T>): Promise<T> => {
  const previous = Tools.PreprocessUrl;
  Tools.PreprocessUrl = (requested: string) => {
    if (requested !== allowed && !requested.startsWith('data:') && !requested.startsWith('blob:')) {
      throw new SceneLoadError(`Scene requested a second URL: ${requested}`);
    }
    return requested;
  };

  try {
    return await load();
  } finally {
    Tools.PreprocessUrl = previous;
  }
};

/** A scene loaded from one self-contained `.babylon` file. */
export const createBabylonSource = (options: BabylonSourceOptions): SceneSource => ({
  id: options.id,
  name: options.name,

  stage: async ({ scene }: StageContext, signal: AbortSignal): Promise<StagedScene> => {
    const url = options.url();
    const own = new Disposables();
    const globals = snapshotGlobals(scene);

    // Fetched here rather than inside the loader so the switcher's abort really
    // cancels the transfer instead of just ignoring the result.
    const response = await fetch(url, { signal }).catch((cause: unknown) => {
      throw new SceneLoadError(`Could not fetch ${options.id}`, cause);
    });
    if (!response.ok) {
      throw new SceneLoadError(`Could not fetch ${options.id}: ${response.status}`);
    }

    // Direct-loaded as text rather than handed over as a File: the loader then
    // needs no FileReader, and there is no second request for it to make.
    const directLoad = `data:${await response.text()}`;

    // The container is not in the scene graph: the previous scene keeps
    // rendering, untouched, until commit.
    const container = await withSingleUrlGuard(url, () =>
      LoadAssetContainerAsync(directLoad, scene, { pluginExtension: '.babylon' })
    ).catch((cause: unknown) => {
      own.dispose();
      throw cause instanceof SceneLoadError ? cause : new SceneLoadError(`Could not parse ${options.id}`, cause);
    });

    signal.throwIfAborted();

    // A file published before the build stopped writing them can carry a
    // runtime render target, restored as an ordinary texture that still claims
    // to be one. The scene calls `_shouldRender()` on anything making that
    // claim, and the throw takes the whole frame with it.
    for (const texture of container.textures) {
      if (texture.isRenderTarget && !(texture instanceof RenderTargetTexture)) {
        texture.isRenderTarget = false;
      }
    }

    // An unregistered node type does not throw inside the loader, it just skips
    // the node; a scene that parsed to nothing is a failure worth surfacing
    // rather than an empty viewport with no explanation.
    if (container.meshes.length === 0) {
      own.dispose();
      throw new SceneLoadError(`${options.id} contained no meshes`);
    }

    const root = new TransformNode(`${options.id}:root`, scene);
    own.add(container, root);

    const loaded: LoadedScene = {
      container,
      mesh: (name) => container.meshes.find((candidate) => candidate.name === name),
      own,
    };
    options.fixups?.(loaded);

    const bounds = boundsOf(container, options.bounds);
    const defaultView = options.defaultView ?? { alpha: -Math.PI / 2, beta: 1.05, radius: bounds.radius * 2.2 };

    return {
      // Shaders compile against a mesh, so this belongs to the staged scene that
      // owns them rather than to the switcher.
      warm: async (budgetMs: number) => {
        const deadline = Date.now() + budgetMs;
        for (const mesh of container.meshes) {
          if (Date.now() > deadline) {
            return;
          }
          await mesh.material?.forceCompilationAsync(mesh).catch(() => undefined);
        }
      },

      commit: (): SceneInstance => {
        container.addAllToScene();
        for (const mesh of container.meshes) {
          // A backdrop stays unparented: Babylon only keeps a skybox centred on
          // the camera while it has no parent, and a projected ground is tied to
          // world y = 0 rather than to anything the world's transform can move.
          if (!mesh.parent && !isBackdrop(mesh)) {
            mesh.parent = root;
          }
        }

        const casters = (options.shadowCasterNames ?? [])
          .map((name) => loaded.mesh(name))
          .filter((mesh): mesh is AbstractMesh => mesh !== undefined);

        const dome = container.meshes.find(
          (mesh) => mesh.material instanceof BackgroundMaterial && mesh.material.enableGroundProjection
        );
        if (dome) {
          const sky = dome.material as BackgroundMaterial;
          // The editor's sky has no mips, so its `.env` carries black ones; sample level 0 as the editor does.
          sky.reflectionTexture?.updateSamplingMode(Texture.BILINEAR_SAMPLINGMODE);
          const extent = extentOfDome(dome);
          const hold = scene.onBeforeRenderObservable.add(() => {
            if (scene.activeCamera) {
              holdProjectedGround(sky, dome, scene.activeCamera, extent);
            }
          });
          own.add({ dispose: () => scene.onBeforeRenderObservable.remove(hold) });
        }

        return createSceneInstance({
          sceneId: options.id,
          scene,
          root,
          bounds,
          defaultView,
          shadowCasters: casters,
          own,
          globals,
        });
      },

      abandon: () => own.dispose(),
    };
  },
});
